import React from "react";
import { Link, Route, Switch, useRouteMatch } from "polyrouter";
import { useTranslation } from "utilities/i18n";
import RegisterDevice from "./RegisterDevice";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import { supportedDevicesPin } from "Constants";
import {
  HardwareModel,
  useSupportsProvisioningOnboardingQuery,
  useSupportsUsbOnboardingQuery
} from "generated/graphql";
import { LoadingIndicator } from "elements";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

const RegisterDecisionFlow: React.FC = () => {
  const { t } = useTranslation();
  const tenantId = useTenantId();

  return (
    <div className="fixed-center register-decision">
      <p>{t("CHOOSE_REGISTRATION")}</p>
      <div className="card decision-content">
        <div className="option">
          <p className="title">{t("PINCODE_ONBOARDING_TITLE")}</p>
          <div className="details mb-1">{t("PINCODE_ONBOARDING_INFO")}</div>
          <div>
            <Link nest to="/supported/pin">
              {t("SUPPORTED_DEVICES")}
            </Link>
          </div>
          <Link nest to="/pin" className="btn btn-primary" data-testid="single">
            {t("REGISTER_SINGLE_DEVICE")}
          </Link>
        </div>
        <div className="option">
          <p className="title">{t("PROVISIONING_TITLE")}</p>
          <div className="details mb-1">{t("PROVISIONING_ONBOARD_INFO")}</div>
          <div>
            <Link nest to="/supported/provision">
              {t("SUPPORTED_DEVICES")}
            </Link>
          </div>
          <Link
            to={`/admin/accounts/details/${tenantId}/provisioning`}
            className="btn btn-primary"
            data-testid="provisioning"
          >
            {t("VISIT_PROVISIONING")}
          </Link>
        </div>
        <div className="option">
          <p className="title">{t("PERSONAL_ONBOARD_TITLE")}</p>
          <div className="details mb-1">{t("PERSONAL_ONBOARD_INFO")}</div>
          <div>
            <Link nest to="/supported/personal">
              {t("SUPPORTED_DEVICES")}
            </Link>
          </div>
          <Link
            to={`/admin/accounts/details/${tenantId}/desktop`}
            className="btn btn-primary"
            data-testid="personal"
          >
            {t("DEPLOY_DESKTOP_APP")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const SupportedDevicesModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    params: { mode }
  } = useRouteMatch<{ mode: string }>();
  const closeModal = useModalDispatch();
  const {
    data: provisioningSupportedDevices,
    fetching: fetchingProvisioningSupportedDevices
  } = useSupportsProvisioningOnboardingQuery({
    pause: mode === "pin" || mode === "personal"
  });
  const {
    data: usbSupportedDevices,
    fetching: fetchingUsbSupportedDevices
  } = useSupportsUsbOnboardingQuery({
    pause: mode === "pin" || mode === "provision"
  });

  const fetching =
    fetchingUsbSupportedDevices || fetchingProvisioningSupportedDevices;

  const supportedModels = (
    modelsList: Pick<HardwareModel, "id" | "name">[] | undefined
  ) => {
    return modelsList?.map(({ name }) => name).sort() ?? [];
  };

  let models: string[], info: string;
  switch (mode) {
    case "pin":
      models = supportedDevicesPin;
      info = t("PINCODE_SUPPORTED_DEVICES_INFO");
      break;
    case "provision":
      models = supportedModels(provisioningSupportedDevices?.hardwareModels);
      info = t("PROV_SUPPORTED_DEVICES_INFO");
      break;
    case "personal":
      models = supportedModels(
        usbSupportedDevices?.hardwareModels.filter(
          ({ skus }) => skus && skus.length !== 0
        )
      );
      info = t("PERSONAL_SUPPORTED_DEVICES_INFO");
      break;
    default:
      return null;
  }

  return (
    <DefaultModalForm
      name="SupportedDevices"
      title={fetching ? "" : t("SUPPORTED_DEVICES")}
      onSubmit={closeModal}
      className="overflow supported-device-modal"
    >
      {fetching ? (
        <LoadingIndicator />
      ) : (
        <>
          <p className="mt-5">{info}</p>
          <div className="supported-device-list">
            {models.map((model, i) => (
              <p key={i}>{model}</p>
            ))}
          </div>

          <div className="btn-container pt-1">
            <button type="submit" className="btn btn-primary">
              {t("OK")}
            </button>
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};

const RegisterDeviceRouter: React.FC = () => (
  <>
    <Route exact path="/manage/register">
      <RegisterDecisionFlow />
    </Route>
    <Switch>
      <Route nest path="/supported/:mode">
        <RegisterDecisionFlow />
        <SupportedDevicesModal />
      </Route>
      <Route nest path="/pin">
        <RegisterDevice />
      </Route>
    </Switch>
  </>
);

export default RegisterDeviceRouter;
